"use client";

import Grid from "@mui/material/Grid2";
import { useFormikContext } from "formik";
import React, { useEffect, useState } from "react";

import FormField from "@/components/atoms/FormField";
import MDBox from "@/components/atoms/MDBox";
import MDTypography from "@/components/atoms/MDTypography";
import { ProductFormData, SkuFormData } from "@/components/screens/ProductFormPage/types";
import { mapSkuVariations as mapSkuVar } from "@/components/templates/SkuVariationForm/form-data";
import {
  defaultPageNumber as pageNumber,
  defaultPageSize as pageSize,
} from "@/constants/pagination";
import useGetProductVariations from "@/hooks/requests/product-variations/use-get-product-variations";
import useFilters from "@/hooks/use-filters";
import { IVariation } from "@/interfaces/sku.interface";
import IProductVariation from "@/interfaces/variation.interface";
import { SkuFormMessages } from "@/messages/sku/sku-form.messages";
import { FilterProperty, FilterType } from "@/types/filter.types";

import { initialVariationFilterValues as initialValue } from "../../../../constants";
import {
  getSkuVariationsFromString,
  getVariationValue,
  initializeEmptyVariationsForNewSku,
  mergeVariationsForEditing,
  serializeVariations,
  updateVariationValue,
} from "./helpers";

type Props = {
  messages: SkuFormMessages;
  product: ProductFormData;
  isEditingSku: boolean;
};

export interface UniVar {
  code: string;
  name: string;
  value?: string;
}

export default function Variations({ messages, product, isEditingSku }: Props) {
  const [variations, setVariations] = useState<IVariation[]>([]);
  const { values, setFieldValue } = useFormikContext<SkuFormData>();

  const { filters, changeFilters } = useFilters<IProductVariation>({ initialValue });
  const { data: fetchedVariations } = useGetProductVariations({ filters, pageNumber, pageSize });

  useEffect(() => {
    const { variationCode, variations: productVariations } = product;

    if (isEditingSku) {
      const skuVariationsString = values.variations as string;

      if (variationCode && productVariations && productVariations.length > 0) {
        const mergedVariations = mergeVariationsForEditing(productVariations, skuVariationsString);
        setVariations(mergedVariations);
        setFieldValue("variations", serializeVariations(mergedVariations));
      } else if (skuVariationsString && skuVariationsString.trim() !== "") {
        const currentSkuVariations = getSkuVariationsFromString(skuVariationsString);
        setVariations(currentSkuVariations);
      } else {
        setVariations([]);
      }
    } else {
      if (variationCode) {
        const variationCodes = variationCode.split(",").map((code) => ({
          property: "id" as FilterProperty<IProductVariation>,
          value: code,
          filterType: FilterType.Contains,
        }));

        changeFilters(variationCodes);
      } else {
        setVariations([]);
      }
    }
  }, [product, values, isEditingSku, changeFilters, setFieldValue]);

  useEffect(() => {
    if (!isEditingSku && fetchedVariations) {
      const variationCode = product.variationCode;
      const variationCodes = variationCode ? variationCode.split(",") : [];
      const updatedVariations = initializeEmptyVariationsForNewSku(
        variationCodes,
        fetchedVariations.items
      );

      setVariations(updatedVariations);
      setFieldValue("variations", serializeVariations(updatedVariations));
    }
  }, [fetchedVariations, product, isEditingSku, setFieldValue]);

  const varOptions: UniVar[] = mapSkuVar(variations);

  const handleVariationChange = (
    code: number,
    name: string,
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = event.target.value;
    const updatedVariations = updateVariationValue(variations, code, name, value);

    setVariations(updatedVariations);
    setFieldValue("variations", serializeVariations(updatedVariations));
  };

  return (
    <MDBox p={3}>
      <MDBox display="flex" justifyContent="space-between">
        <MDTypography variant="h5" fontWeight={"bold"}>
          {messages.variationTitle}
        </MDTypography>
      </MDBox>

      <MDBox mt={1}>
        <Grid container spacing={3}>
          {varOptions.map(({ code, name }) => (
            <Grid key={code} size={{ md: 2, xs: 12 }}>
              <FormField
                variant="standard"
                name={`variations[${code}]`}
                label={name}
                value={getVariationValue(variations, Number(code))}
                onChange={(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                  handleVariationChange(Number(code), name, event)
                }
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          ))}
        </Grid>
      </MDBox>
    </MDBox>
  );
}
